import React from 'react';
import {
    CHANGE_LOCATION, LOCAL_CITY_LOCATION,
    YOUR_DETECTED_LOCATION,
    YOUR_SELECTED_LOCATION,
    AVERAGE_RATE_THRESHOLD,
} from '../../constants/constants';
import ChangeLocationDrawer from './change-location-drawer';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import { RatingSummary } from '../atoms/RatingSummary';
import { colors } from '../primitives/colors';
import RowSet from '../primitives/grid/row-set';
import Skeleton from '../primitives/skeleton';
import { StyledButtonAsLink } from '../primitives/styled-button';
import StyledPillButton from '../primitives/styled-pill-button';
import { breakpoints, borderRadius } from '../primitives/tokens';
import Typography from '../primitives/typography';
import Link from '../primitives/link';
import FadeTransition from '../animations/transitions/fade-transition';
import PhoneNumber from '../molecules/phone-number';
import { useLocator } from '../../contexts/franchise/locator-provider';
import formatPhoneLink from '../utils/format-phone-link';

const NearestVertical = (): JSX.Element => {
    const { geo, franchise: locatorFranchise, locationChangedByUser, isNationalCallCenter, loadingFirst: locatorLoadingFirstResult } = useLocator();
    const { isMicrosite, isLocalCityPage, ...microsite } = useMicrosite();

    const [open, setOpen] = React.useState(false);

    const franchise = microsite?.franchise || locatorFranchise?.data;
    const name = franchise?.name;
    const averageRating = franchise?.averageRating;
    const permalink = franchise?.permalink;
    const reviewCount = franchise?.reviewCount;
    const phone = franchise?.mainPhone;

    // This is a more accurate loading state, as 'loading' from useLocator will take into account -all- 5 franchise options being loaded.
    const loading = !name || !franchise || !phone || locatorLoadingFirstResult;
    const shouldDisplayReview = !loading && reviewCount !== 0 && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD;

    const firstLineText = (() => {
        if (isMicrosite) {
            return name;
        }

        if (isLocalCityPage) {
            return `${microsite?.targetCity?.city || microsite?.franchise?.address?.city}, ${microsite?.targetCity?.state}`;
        }

        if (!loading) {
            if (locationChangedByUser) {
                return name;
            }

            if (!locationChangedByUser && !!geo && !!geo.city) {
                return `${geo?.city}${geo?.stateShort ? `, ${geo.stateShort}` : ''}`;
            }
        }

        return '';
    })();

    const firstLineLink: string|undefined = (() => {
        if (isMicrosite) {
            return undefined;
        }
        if (isLocalCityPage) {
            return undefined;
        }
        if (name && locationChangedByUser) {
            return permalink;
        }
        return undefined;
    })();

    const FirstLineComponent = firstLineLink ? Link : 'h1';

    const hasSecondLineText = (() => {
        if (isMicrosite) {
            return false;
        }
        if (isLocalCityPage) {
            return true;
        }
        if (name && locationChangedByUser) {
            return false;
        }
        return true;
    })();

    const secondLineText = (() => {
        if (isMicrosite) {
            return '';
        }
        if (isLocalCityPage) {
            return name;
        }
        if (!loading) {
            if (!locationChangedByUser && isNationalCallCenter) {
                return 'For service in your area, please call our National Call Center.';
            }
            if (name && !locationChangedByUser) {
                return name;
            }
            return '';
        }
        return '';
    })();

    const secondLineLink: string|undefined = (() => {
        if (isMicrosite) {
            return undefined;
        }
        if (isLocalCityPage) {
            return permalink;
        }
        if (name && !locationChangedByUser) {
            return permalink;
        }
        return undefined;
    })();

    const SecondLineComponent = secondLineLink ? Link : 'h2';

    return (
        <>
            <div css={{
                width: '100%',
                backgroundColor: colors.shadesWhite,
                borderRadius: borderRadius.small,
                boxShadow: '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
                padding: '16px',
                [breakpoints.tb]: {
                    padding: '18px 24px',
                },
            }}
            >
                <Typography
                    fontSize={{
                        dt: 'base',
                    }}
                    css={{
                        lineHeight: '1.2',
                    }}
                    fontWeight="medium"
                    color="primaryOrange"
                >
                    24/7 Emergency Service
                </Typography>
                {phone && (
                    <PhoneNumber
                        css={{
                            marginTop: '8px',
                        }}
                        as={StyledButtonAsLink}
                        to={`tel:${formatPhoneLink(phone) as string}`}
                    >
                        {formatPhoneLink(phone)}
                    </PhoneNumber>
                )}
                <RowSet
                    breakpoints={{ dt: { between: 4 } }}
                    css={{
                        marginTop: '24px',
                    }}
                >
                    <Typography
                        fontSize={{
                            dt: 'sm',
                        }}
                        color="primaryOrange"
                        fontWeight="regular"
                        css={{
                            marginBottom: '5px',
                        }}
                    >
                        {isMicrosite && 'Here to Help® 24/7.'}
                        {isLocalCityPage && LOCAL_CITY_LOCATION}
                        {(!isMicrosite && !isLocalCityPage && locationChangedByUser && !isLocalCityPage) && YOUR_SELECTED_LOCATION}
                        {(!isMicrosite && !isLocalCityPage && !locationChangedByUser && !isLocalCityPage) && YOUR_DETECTED_LOCATION}
                    </Typography>

                    <div
                        css={{
                            marginBottom: '8px',
                            a: {
                                textDecoration: 'underline',
                                textDecorationThickness: firstLineLink ? '2px' : '1px',
                                textUnderlineOffset: '3px',
                            },
                        }}
                    >
                        <Typography
                            as={FirstLineComponent}
                            to={firstLineLink}
                            fontSize={{
                                dt: '2xl',
                            }}
                            fontWeight="regular"
                        >
                            <Skeleton css={{
                                width: '200px',
                                marginBottom: '6px',
                            }}
                            >
                                {firstLineText}
                            </Skeleton>
                        </Typography>
                        {hasSecondLineText && (
                            <Typography
                                as={SecondLineComponent}
                                to={secondLineLink}
                                fontSize={{
                                    dt: 'base',
                                }}
                                css={{
                                    marginTop: '4px',
                                }}
                            >
                                <Skeleton css={{
                                    width: '300px',
                                }}
                                >
                                    {secondLineText || null}
                                </Skeleton>
                            </Typography>
                        )}
                    </div>
                    <FadeTransition shouldChange={shouldDisplayReview.toString()}>
                        <div
                            css={{
                                display: 'flex',
                                gap: '4px',
                                marginTop: '12px',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                            }}
                        >

                            <Skeleton css={{
                                height: '24px',
                                width: '230px',
                            }}
                            >
                                {shouldDisplayReview && (
                                    <RatingSummary
                                        rating={averageRating}
                                        count={reviewCount}
                                    />
                                )}
                                {!shouldDisplayReview && !loading && (
                                    <div />
                                )}
                            </Skeleton>
                            {(!isLocalCityPage && !isMicrosite) && (
                                <div
                                    className="change-location-open-cta"
                                    css={{
                                        marginTop: '8px',
                                    }}
                                >
                                    <Skeleton css={{
                                        width: '100px',
                                    }}
                                    >
                                        {!loading && (
                                            <div css={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                transition: '0.8s',
                                                [breakpoints.tb]: {
                                                    gap: '8px',
                                                },
                                            }}
                                            >
                                                <span>Need help elsewhere?</span>
                                                {' '}
                                                <StyledPillButton onClick={() => {
                                                    setOpen(true);
                                                }}
                                                >
                                                    {CHANGE_LOCATION}
                                                </StyledPillButton>
                                            </div>
                                        )}
                                    </Skeleton>
                                </div>
                            )}
                        </div>
                    </FadeTransition>
                </RowSet>
            </div>

            <ChangeLocationDrawer
                visible={open}
                onClose={() => setOpen(false)}
            />
        </>
    );
};

export default NearestVertical;
